<template>
    <div id="app">
        <Toast />

        <router-view v-slot="{ Component }">
            <transition
                name="fade"
                mode="out-in"
            >
                <component :is="Component" />
            </transition>
        </router-view>
    </div>
</template>

<script>
    import { defineComponent, onMounted, watch, onUnmounted } from 'vue'
    import Toast from '@/components/ui/Toast.vue'
    import { useEmpresaStore } from '@/stores/empresa'
    import { useUsuarioStore } from '@/stores/usuario'
    import { useRouter, useRoute } from 'vue-router'

    export default defineComponent({
        name: 'App',
        components: {
            Toast,
        },
        setup() {
            const router = useRouter()
            const route = useRoute()
            const usuarioStore = useUsuarioStore()
            const empresaStore = useEmpresaStore()

            const MOBILE_BREAKPOINT = 1149

            const verificarAcessoPorTela = () => {
                const rotasIgnoradas = ['Login', 'Error403', 'Error404', 'MobileError']
                if (!usuarioStore.dadosUsuario || rotasIgnoradas.includes(route.name)) return

                // Verifica se está em Desktop ou em modo Paisagem (Landscape)
                const isDesktopOrLandscape =
                    window.innerWidth > 1149 ||
                    window.matchMedia('(orientation: landscape)').matches

                const empresaAtual = empresaStore.empresaAtual
                if (!empresaAtual) return

                // Administradores não sofrem bloqueio de tela
                if (['super', 'adm'].includes(empresaAtual.system_role)) return

                const permissions = empresaAtual.access || []

                // Função auxiliar para checar se o módulo atual é restrito
                const isRestricted = (modName, actionName) => {
                    const perm = permissions.find(
                        (p) => p.mod_name === modName && p.action_name === actionName,
                    )
                    // Se a permissão diz 'portrait' e a tela está deitada ou no PC, bloqueia
                    return perm && perm.access_type === 'portrait' && isDesktopOrLandscape
                }

                let deveBloquear = false

                // Mapeamento de Rotas -> Módulos para checagem
                const travelRequesterRoutes = ['travel', 'TravelFormPageNew', 'TravelFormPageEdit']
                const travelApproverRoutes = ['travel-approvals', 'TravelFormPageApproverView']
                const finRequesterRoutes = ['FinancialControl', 'MyReleasesNew', 'MyReleasesEdit']
                const finApproverRoutes = ['FinancialControlApprover', 'MyReleasesApproveView']
                const finPayerRoutes = ['FinancialControlPayer', 'MyReleasesPayerView']

                // Valida a rota atual e verifica se deve bloquear
                if (travelRequesterRoutes.includes(route.name))
                    deveBloquear = isRestricted('travel', 'requester')
                else if (travelApproverRoutes.includes(route.name))
                    deveBloquear = isRestricted('travel', 'approver')
                else if (finRequesterRoutes.includes(route.name))
                    deveBloquear = isRestricted('financial', 'requester') // Ajuste o 'mod_name' conforme vem do back
                else if (finApproverRoutes.includes(route.name))
                    deveBloquear = isRestricted('financial', 'approver')
                else if (finPayerRoutes.includes(route.name))
                    deveBloquear = isRestricted('financial', 'payer')
                // Adicione outros módulos futuros aqui

                if (deveBloquear) {
                    router.push(`/${usuarioStore.dadosUsuario.idioma || 'pt'}/mobile-error`)
                }
            }

            onMounted(() => {
                window.addEventListener('resize', verificarAcessoPorTela)
            })

            onUnmounted(() => {
                window.removeEventListener('resize', verificarAcessoPorTela)
            })

            watch(
                () => usuarioStore.dadosUsuario.idioma,
                (novoIdioma) => {
                    if (novoIdioma) {
                        usuarioStore.setLocale(novoIdioma)
                    }
                },
                { immediate: true },
            )

            watch(
                () => [
                    empresaStore.empresaAtual?.user_company_uuid,
                    empresaStore.empresaAtual?.company_color,
                ],
                () => {
                    empresaStore.applyActiveTheme()
                },
                { immediate: true },
            )

            watch(route, () => {
                verificarAcessoPorTela()
            })

            return {}
        },
    })
</script>

<style lang="scss">
    #app {
        min-height: 100vh;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
    .vs__dropdown-menu {
        color: #464646 !important;
        margin-top: 0.5rem !important;
        border-radius: 0.25rem 0.25rem 1rem 1rem !important;
        box-shadow: 0 0.4vh 0.4vh rgba(0, 0, 0, 0.15) !important;
        border: 1px solid #e0e0e0 !important;
        padding: 0 !important;
        max-height: 15rem !important;
        z-index: 99999 !important;
        scrollbar-width: none;
    }

    .vs__dropdown-option {
        padding: 0.8rem 1.3rem !important;
        font-size: 0.8rem !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        border-bottom: 1px solid #f0f0f0;
    }

    .vs__dropdown-option:last-child {
        border-bottom: none;
    }

    .vs__dropdown-option--highlight {
        background: var(--colorOpacity5) !important;
        color: black !important;
    }
</style>
